import { FunctionComponent, memo, useLayoutEffect, useMemo, useRef } from 'react';
import { Group, Vector3 } from 'three';
import { Jute } from '../..';
import { transitionManager } from '../../hooks';
import { useEnterOrExit, useIsSceneFlatImageTracked } from '../../hooks/general';
import { useDataStore, useSceneStore } from '../../zustand';
import { ISceneComp, ISpatialComponentUnion, ITransitionData, ITransitionEffectTypes, ITuple3 } from '@r3f-component-data-structure';
import FadeGroup from '../FadeGroup';
import { getOpacityTransitionRange, getSpatialTransitionRange, IEasingTypes, ITransCatTypes, Transition } from '@r3f/transitions';
import { ThreeElements, useFrame } from '@react-three/fiber';
import { ITransitionSyncState, syncTransitionConfig, transitionSignature } from '../../utils/transitionSync';

type IEntityLevelFadeGroupProps = ThreeElements['group'] & {
	sceneId: string;
	entityId: string;
	position: ITuple3;
	screenRelative: boolean;
};

const EntityLevelFadeGroup: FunctionComponent<IEntityLevelFadeGroupProps> = ({ children, sceneId, entityId: id, screenRelative, position, ...rest }) => {
	const fadeGroupRef = useRef<Group>(null);
	const transitionCategory = useSceneStore(({ transitionCategory }) => transitionCategory[sceneId]);
	const placementMode = useSceneStore(({ placementMode }) => placementMode);
	const componentsById = useDataStore(({ componentsById }) => componentsById);
	const entity = componentsById ? (componentsById[id] as ISpatialComponentUnion) : undefined;
	const scene = componentsById ? (componentsById[sceneId] as ISceneComp) : undefined;
	const enterOrExit = useEnterOrExit(transitionCategory);
	const parentSceneIsFlatImageTracked = useIsSceneFlatImageTracked(scene);

	const isTestMode = Jute.environment === 'test';
	const entitySpatialTransition = useMemo(
		() => new Transition<ITuple3>({ start: [0, 0, 0], end: [0, 0, 0], duration: 0, easingType: IEasingTypes.linear, delay: 0, isTestMode, transitionId: `spatial-${id}` }),
		[]
	); // eslint-disable-line react-hooks/exhaustive-deps
	const entityOpacityTransition = useMemo(() => new Transition<number>({ start: 0, end: 0, duration: 0, easingType: IEasingTypes.linear, delay: 0, isTestMode, transitionId: `opacity-${id}` }), []); // eslint-disable-line react-hooks/exhaustive-deps

	useFrame(() => {
		entityOpacityTransition.tick();
		entitySpatialTransition.tick();
	})

	const lastOpacityConfig = useRef<ITransitionSyncState>({});
	const lastSpatialConfig = useRef<ITransitionSyncState>({});

	/**
	 * Everything about this entity that its enter/exit transition depends on.
	 *
	 * The transform is in here even though it never reaches `set()`: the fade group sits at the origin and
	 * the entity's own transform is applied on an inner group, so a position or scale edit changes where
	 * the transition lands without changing its from/to. It still has to count as a change, otherwise an
	 * entity edited mid-flight keeps sliding towards where it used to be.
	 */
	const entityConfigSignature = useMemo(
		() => (entity ? transitionSignature(entity.position, entity.scale, entity.rotation, entity.transitions?.[enterOrExit]) : ''),
		[entity, enterOrExit]
	);

	// Register this entity's spatial transition with its scene's group, and unregister it on unmount.
	//
	// This used to be guarded by a module-level `transitionAddedDict[id]` that outlived the component,
	// against a `TransitionManager` that was push-only. A deleted entity therefore left a dead Transition
	// in the group AND a `true` that would refuse to re-register the same id — and since the group's
	// callbacks only fire once every member has reported, one orphan stopped `onStartAll` / `onFinishAll`
	// for the whole scene. `add` now de-duplicates by identity, so the dictionary is gone.
	//
	// This does NOT make the component safe to remount on content change — see the Hazards section of
	// PROJECT_JUTE_PREVIEW.md; `FadeGroup`'s opacity snapshot and `PlacementScene`'s opacity multiply are
	// still once-only. The deliberate design remains that entities update in place and never remount.
	useLayoutEffect(() => {
		if (screenRelative) return;
		transitionManager[sceneId].add(entitySpatialTransition);

		return () => {
			transitionManager[sceneId]?.remove(entitySpatialTransition);
		};
	}, [entitySpatialTransition, sceneId, id, screenRelative]);

	// use transition category to set opacity transition
	useLayoutEffect(() => {
		if (transitionCategory === ITransCatTypes.hidden || screenRelative || !entity || !scene) return;
		const { transitions } = entity;
		const noEntityExitTrans = !transitions?.[enterOrExit] && transitionCategory === ITransCatTypes.exit;
		const noSceneExitTrans = !scene.transitions?.[enterOrExit] && transitionCategory === ITransCatTypes.exit;
		const noEntityEnterTrans = !transitions?.[enterOrExit] && transitionCategory === ITransCatTypes.enter;
		if (noEntityExitTrans && !noSceneExitTrans) return; // don't let the entity disapear before scene transition finishes
		let { delay = 0, duration = 0 } = (transitions?.[enterOrExit] as ITransitionData) || {};
		let { to, from } = getOpacityTransitionRange(transitionCategory);
		if (noEntityEnterTrans || noEntityExitTrans) {
			to = 1;
			from = 1;
			duration = 0.2;
		}
		// Configure always, trigger only on a navigation or on an edit that landed mid-fade. Before this
		// split, every live edit anywhere in the scene re-ran `start()` here and the whole scene visibly
		// faded in again. See PROJECT_JUTE_PREVIEW.md.
		const { apply, navigated, restart } = syncTransitionConfig(
			lastOpacityConfig,
			entityOpacityTransition,
			transitionCategory,
			transitionSignature(entityConfigSignature, from, to, delay, duration)
		);
		if (!apply) return;

		entityOpacityTransition.set({ to, from, delay, duration });
		if (navigated || restart) entityOpacityTransition.start();
	}, [enterOrExit, entityOpacityTransition, entity, transitionCategory, id, fadeGroupRef, scene, entityConfigSignature]); // eslint-disable-line react-hooks/exhaustive-deps

	// use transition category to set spatial transition
	useLayoutEffect(() => {
		if (transitionCategory === ITransCatTypes.hidden || screenRelative || !entity) return;

		const { transitions } = entity;
		let { type: effect = ITransitionEffectTypes.up, delay = 0, duration = 0 } = (transitions?.[enterOrExit] as ITransitionData) || {};
		if (!fadeGroupRef.current) return;
		const { x, y, z } = fadeGroupRef.current?.position as Vector3;
		const [sx, sy, sz] = transitionCategory === ITransCatTypes.exit ? [x, y, z] : position;

		// const [sx, sy, sz] = entity.position;
		const [ex, ey, ez] = position;
		let { to, from, threeProperty } = getSpatialTransitionRange({
			startPosition: [sx, sy, sz],
			enterEndPosition: [ex, ey, ez],
			effect,
			category: transitionCategory,
			positionOffset: 2,
			sceneIsFlatImageTracked: parentSceneIsFlatImageTracked
		});

		if (!transitions?.[enterOrExit]?.duration) {
			switch (threeProperty) {
				case 'position':
					from = [ex, ey, ez];
					to = [ex, ey, ez];
					break;
				case 'scale':
					from = [1, 1, 1];
					to = [1, 1, 1];
			}
		}

		// TODO: check why scale not affected and if fade out really needed
		// if scene has a fade transition after a spatial transition set the transition parameters to have no spatial effect
		if (effect === ITransitionEffectTypes.fadeIn || effect === ITransitionEffectTypes.fadeOut) {
			from = [ex, ey, ez];
			to = [ex, ey, ez];
			duration = 0;
		}

		const { apply, restart } = syncTransitionConfig(
			lastSpatialConfig,
			entitySpatialTransition,
			transitionCategory,
			transitionSignature(entityConfigSignature, from, to, delay, duration, threeProperty)
		);
		// Nothing this transition is built from changed — leave it alone. Reconfiguring regardless is not
		// free: `set()` stops a running transition, so an unrelated edit used to freeze this entity
		// wherever it happened to be.
		if (!apply) return;

		// if the entity had a position or scale changing transition on exit and no position or scale changing
		// transition respectively on enter reset position/scale to the right values before enter
		if (transitionCategory === ITransCatTypes.enter) {
			switch (threeProperty) {
				case 'scale':
					fadeGroupRef.current?.position?.set?.(...(position as ITuple3));
					break;
				default:
					fadeGroupRef.current?.scale?.set?.(1, 1, 1);
					fadeGroupRef.current?.position?.set?.(...(position as ITuple3));
					break;
			}
		}

		entitySpatialTransition.set({ to, from, delay, duration });
		entitySpatialTransition.onChange((value) => {
			if (threeProperty === 'none') return;
			fadeGroupRef.current?.[threeProperty]?.set?.(...value);
		});

		// On a navigation the scene starts this one for us, through `transitionManager[sceneId].startAll()`
		// — starting it here as well would double-fire `onStart`. A content edit has no such trigger, so a
		// transition caught mid-flight has to be restarted here.
		if (restart) entitySpatialTransition.start();
	}, [enterOrExit, entitySpatialTransition, transitionCategory, entity, fadeGroupRef, parentSceneIsFlatImageTracked, entityConfigSignature]); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			{!!entity ? (
				<FadeGroup
					ref={fadeGroupRef}
					position={position}
					transition={entityOpacityTransition}
					visible={transitionCategory !== ITransCatTypes.hidden && !placementMode}
					transitionCategory={transitionCategory}
					screenRelativeEntityLevel={screenRelative}
					{...rest}
				>
					{children}
				</FadeGroup>
			) : null}
		</>
	);
};

EntityLevelFadeGroup.displayName = 'EntityLevelFadeGroup';
export default memo(EntityLevelFadeGroup);
